import { createRouter, createWebHashHistory } from 'vue-router'
import routes from './routes'
import { aigcStore } from '@/store/aigcStore'
import { knowStore } from '@/store/knowStore'
import { loginStore } from '@/store/loginStore'
import { localCache, sessionCache } from '@/utils/cache'
import { checkBrowser } from '@/utils/checkStatus'
import { isMobile, isPhoneDevice } from '@/utils/validation'
import { isDemoUser } from '@/utils/menuDisplayConfig'

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes
})

/* 手机端已适配的页面（按路由 name）：登录页、用户协议、隐私保护协议，
   以及三个免登录分享页（创意脚本、Story board、分镜视频画布，页面里按 ≤768px 做了移动端版式）。
   其余页面在手机上打开统一进状态页 pageStatus/mobileUnsupported；状态页自身也放行，否则会循环跳转 */
const MOBILE_READY_ROUTES = [
  'login',
  'userAgreement',
  'privacyAgreement',
  'pageStatus',
  'videoIdeaScriptShare',
  'videoStoryboardShare',
  'shotVideoCanvasShare'
]
const MOBILE_UNSUPPORTED = { name: 'pageStatus', params: { code: 'mobileUnsupported' } }

// 纯静态提示页，不需要登录：未登录用户也应直接看到提示，而不是被带去登录页
const STATIC_STATUS_CODES = ['mobileUnsupported', 'browserVersionLow']
const isStaticStatusPage = (to) =>
  to.name === 'pageStatus' && STATIC_STATUS_CODES.includes(to.params.code)

const closeModal = () => {
  const store = aigcStore()
  const kStore = knowStore()
  store.closeShareFolderModal()
  kStore.closeShareFolderModal()
}
// 命中低版本浏览器时已调用 next 完成跳转，返回 true 让调用方提前结束，
// 避免 beforeEach 继续执行到底部再调一次 next
const checkBrowserHandle = (to, from, next) => {
  /* 手机端不走 PC 的版本门槛（Chrome <87 等）：手机只开放登录、协议等已适配页面，
     这些页面已按 .browserslistrc 的下限转译并做了旧内核兼容；其余页面由下方的手机端拦截统一进提示页。
     否则安卓旧内核打开登录页只能看到「浏览器版本低」，打开业务页又被这里抢先拦走 */
  if (isPhoneDevice()) return false
  //判断跳转到状态页
  if (!checkBrowser() && to.name !== 'pageStatus') {
    // 跳转到不支持页面
    next({
      name: 'pageStatus',
      params: {
        code: 'browserVersionLow'
      }
    })
    return true
  }
  return false
}

router.beforeEach(async (to, from, next) => {
  closeModal()
  if (checkBrowserHandle(to, from, next)) return
  // 如果是移动端打开就跳去移动端页面
  if (isMobile() && to.meta.fixurl) {
    window.location.href = to.meta.fixurl(to)
    return
  } else if (to.name === 'InvitePage') {
    if (to.query.teamInviteCode) {
      localCache.setCache('invite', to.query.teamInviteCode)
      const lStore = loginStore()
      lStore.invite = to.query.teamInviteCode
    }
  } else if (to.name === 'knowledgeShareFolder') {
    sessionCache.setCache('knowledgeShareParams', to.params.code)
  }
  /* 手机端未适配页面：放在上面的 H5 跳转（meta.fixurl）之后，有独立 H5 页的分享链接仍去 H5；
     放在登录校验之前，未登录用户也直接看到提示，而不是先被带去登录 */
  if (isPhoneDevice() && !MOBILE_READY_ROUTES.includes(to.name)) {
    next(MOBILE_UNSUPPORTED)
    return
  }
  if (isStaticStatusPage(to)) {
    next()
    return
  }
  if (!localCache.getCache('USER') || !localCache.getCache('TOKEN')) {
    //免登录页面
    if (to.meta.noLogin) {
      next()
    } else {
      next({
        name: 'login'
      })
    }
  } else if (to.meta.demoOnly && !(await isDemoUser())) {
    /* 灰度演示入口：菜单里已按后端下发的显示配置隐藏，这里补上路由侧的同口径拦截，
       否则不可见的用户直接输 URL 就能进，出现「藏了但没挡」的不一致。
       判据由接口给出，但这里拦的只是页面入口，不是访问控制 ——
       最终授权仍须由服务端在各业务接口上判定，见 @/utils/menuDisplayConfig 的说明 */
    next({ name: 'pageStatus', params: { code: 'notFound' } })
  } else {
    next()
  }
})

export default router
